import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ABSENCE_TYPES, todayISO } from "@/lib/format";

const blank = () => ({ technician_id: "", type: "altro", start_date: todayISO(), end_date: todayISO(), partial: false, start_time: "08:00", end_time: "12:00", reason: "" });

export function ManualAbsenceDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank());
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(blank());
    Promise.all([api.get("/admin/technicians"), api.get("/admin/technicians", { params: { role: "responsabile" } })])
      .then(([a, b]) => setStaff([...a.data, ...b.data].filter((t) => t.active)));
  }, [open]);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.technician_id) return toast.error("Scegli il dipendente");
    if (f.reason.trim().length < 2) return toast.error("Indica la motivazione");
    setBusy(true);
    try {
      const { partial, ...rest } = f;
      await api.post("/admin/absences", { ...rest, reason: f.reason.trim(), end_date: partial ? f.start_date : f.end_date,
        start_time: partial ? f.start_time : null, end_time: partial ? f.end_time : null });
      toast.success("Assenza registrata");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="manual-absence-button"><Plus className="h-4 w-4" /> Inserisci assenza</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="manual-absence-dialog">
          <DialogHeader><DialogTitle className="font-display">Inserisci assenza</DialogTitle>
            <DialogDescription>Per chi non ha timbrato perché assente. Viene registrata già approvata.</DialogDescription></DialogHeader>
          <div className="space-y-1"><Label>Dipendente</Label>
            <select value={f.technician_id} onChange={upd("technician_id")} className="w-full rounded-lg border bg-white px-3 py-2 text-sm" data-testid="manual-absence-staff">
              <option value="">Scegli…</option>{staff.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
          <div className="space-y-1"><Label>Tipo</Label>
            <select value={f.type} onChange={upd("type")} className="w-full rounded-lg border bg-white px-3 py-2 text-sm" data-testid="manual-absence-type">
              {Object.entries(ABSENCE_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.partial} onChange={(e) => setF((p) => ({ ...p, partial: e.target.checked }))} data-testid="manual-absence-partial" /> Solo alcune ore</label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{f.partial ? "Giorno" : "Dal"}</Label><Input type="date" value={f.start_date} onChange={upd("start_date")} data-testid="manual-absence-start-date" /></div>
            {f.partial
              ? <div className="grid grid-cols-2 gap-2"><div className="space-y-1"><Label className="text-xs">Dalle</Label><Input type="time" value={f.start_time} onChange={upd("start_time")} data-testid="manual-absence-start-time" /></div>
                <div className="space-y-1"><Label className="text-xs">Alle</Label><Input type="time" value={f.end_time} onChange={upd("end_time")} data-testid="manual-absence-end-time" /></div></div>
              : <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={f.end_date} onChange={upd("end_date")} data-testid="manual-absence-end-date" /></div>}
          </div>
          <div className="space-y-1"><Label>Motivazione *</Label><Textarea rows={2} maxLength={300} value={f.reason} onChange={upd("reason")} placeholder="Es. malattia comunicata per telefono" data-testid="manual-absence-reason" /></div>
          <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full" data-testid="manual-absence-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Registra assenza</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
